/**
 * Planogram ordering as a store-order form option.
 * Quantities write into the same global order as the category tabs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { LayoutGrid, List as ListIcon, Rows, Search } from 'lucide-react';
import { usePlanogram } from '../../hooks/usePlanogram';
import { PlanogramForm, PlanogramProduct, PlanogramSlot } from '../../@types/planogram';
import { ListView, RackView, RackZoom, ShelfView } from '../planogram/PlanogramPanelViews';
import { OrderLine, ProductSheet, ReviewSheet } from '../planogram/PlanogramSheets';
import { matchesQuery, PlanogramFilter } from '../planogram/planogramTheme';
import { useGlobalOrderData } from '../../hooks/useGlobalOrderData';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../styles/ThemeStyles';

type ViewMode = 'rack' | 'shelf' | 'list';

interface PlanogramOrderFormProps {
  storeId: string;
  onShowPreview?: () => void;
}

type ItemBucket = 'vegetableItems' | 'flowerItems' | 'heritageItems' | 'livingstonItems';

function bucketForForm(form: PlanogramForm): ItemBucket | null {
  if (form === 'Vegetable') return 'vegetableItems';
  if (form === 'Flower') return 'flowerItems';
  if (form === 'Heritage') return 'heritageItems';
  if (form === 'Livingston') return 'livingstonItems';
  return null;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  align-items: center;
`;

const Pill = styled.button<{ $active?: boolean }>`
  border: 1px solid ${props => props.$active ? ThemeColors.primary.main : ThemeColors.border.light};
  background: ${props => props.$active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.$active ? 'white' : ThemeColors.text.primary};
  border-radius: ${BorderRadius.full};
  padding: 6px 12px;
  font-size: ${Typography.fontSize.sm};
  cursor: pointer;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 180px;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: 8px 12px 8px 32px;
  font-size: ${Typography.fontSize.sm};
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;
  svg { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: ${ThemeColors.text.secondary}; }
`;

const Hint = styled.p`
  margin: 0;
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
`;

export const PlanogramOrderForm: React.FC<PlanogramOrderFormProps> = ({ storeId }) => {
  const { catalog, loading, error, refetch } = usePlanogram();
  const { orderData, updateOrderData } = useGlobalOrderData();
  const [displayIndex, setDisplayIndex] = useState(0);
  const [panelIndex, setPanelIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('rack');
  const [zoom, setZoom] = useState<RackZoom>('fit');
  const [query, setQuery] = useState('');
  const [filter] = useState<PlanogramFilter>('all');
  const [selectedSlot, setSelectedSlot] = useState<PlanogramSlot | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const skipNextSync = useRef(false);

  const quantities = useMemo(() => {
    const qty: Record<string, number> = {};
    if (!catalog) return qty;
    const allItems = [
      ...(orderData.vegetableItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
    ];
    const byCode = new Map(allItems.map(item => [item.code, item.quantity]));
    Object.values(catalog.productsByKey).forEach(product => {
      if (!product.itemCode) return;
      const n = byCode.get(product.itemCode);
      if (n && n > 0) qty[product.key] = n;
    });
    return qty;
  }, [catalog, orderData.vegetableItems, orderData.flowerItems, orderData.heritageItems, orderData.livingstonItems]);

  const display = catalog?.displays[displayIndex] ?? null;
  const panel = display?.panels[panelIndex] ?? null;

  const setQuantity = useCallback((product: PlanogramProduct, qty: number) => {
    if (product.missing || !product.itemCode) {
      toast.error('This facing has no catalog SKU, so it cannot be ordered.');
      return;
    }
    const bucket = bucketForForm(product.form);
    if (!bucket) {
      toast.error('This facing is a display accessory, not a seed SKU.');
      return;
    }
    skipNextSync.current = true;
    const current = [...((orderData as any)[bucket] || [])];
    const idx = current.findIndex((item: { code: string }) => item.code === product.itemCode);
    if (qty <= 0) {
      if (idx >= 0) current.splice(idx, 1);
    } else if (idx >= 0) {
      current[idx] = { ...current[idx], quantity: qty };
    } else {
      current.push({
        id: product.itemCode,
        packetDescription: product.name,
        code: product.itemCode,
        quantity: qty,
        price: product.price ?? 3.99,
        category: product.form.toLowerCase(),
      });
    }
    updateOrderData({ [bucket]: current });
  }, [orderData, updateOrderData]);

  useEffect(() => {
    skipNextSync.current = false;
  }, [storeId]);

  const lines = useMemo(() => {
    if (!catalog) return [];
    return Object.entries(quantities)
      .map(([key, qty]) => ({ product: catalog.productsByKey[key], qty }))
      .filter((line) => Boolean(line.product))
      .sort((a, b) => a.product.name.localeCompare(b.product.name));
  }, [catalog, quantities]);

  if (loading) return <Hint>Loading planogram layout…</Hint>;
  if (error || !catalog || !display || !panel) {
    return (
      <Hint>
        Could not load the planogram.{' '}
        <button type="button" onClick={refetch}>Retry</button>
      </Hint>
    );
  }

  const viewProps = {
    panel,
    quantities,
    onSelectSlot: setSelectedSlot,
    onChangeQuantity: setQuantity,
    query,
    filter,
  };

  return (
    <Wrap>
      <Hint>Order from the rack layout. Quantities are added to this store order and show up in Preview Order.</Hint>
      <Toolbar>
        {catalog.displays.map((option, index) => (
          <Pill
            key={option.id}
            type="button"
            $active={index === displayIndex}
            onClick={() => { setDisplayIndex(index); setPanelIndex(0); }}
          >
            {option.short}
          </Pill>
        ))}
        <Pill type="button" $active={viewMode === 'rack'} onClick={() => setViewMode('rack')}><LayoutGrid size={14} /></Pill>
        <Pill type="button" $active={viewMode === 'shelf'} onClick={() => setViewMode('shelf')}><Rows size={14} /></Pill>
        <Pill type="button" $active={viewMode === 'list'} onClick={() => setViewMode('list')}><ListIcon size={14} /></Pill>
        {viewMode === 'rack' && (['fit', 'md', 'lg'] as RackZoom[]).map((level) => (
          <Pill key={level} type="button" $active={zoom === level} onClick={() => setZoom(level)}>
            {level === 'fit' ? 'Fit' : level.toUpperCase()}
          </Pill>
        ))}
        <SearchWrap>
          <Search size={14} />
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this rack…"
          />
        </SearchWrap>
        {display.panels.map((option, index) => (
          <Pill
            key={option.label}
            type="button"
            $active={index === panelIndex}
            onClick={() => setPanelIndex(index)}
          >
            Panel {option.letter}
          </Pill>
        ))}
        {lines.length > 0 && (
          <Pill type="button" $active onClick={() => setReviewOpen(true)}>
            {lines.reduce((sum, line) => sum + line.qty, 0)} on order
          </Pill>
        )}
      </Toolbar>
      {viewMode === 'rack' && <RackView {...viewProps} zoom={zoom} />}
      {viewMode === 'shelf' && <ShelfView {...viewProps} />}
      {viewMode === 'list' && <ListView {...viewProps} />}
      {selectedSlot && (
        <ProductSheet
          product={selectedSlot.product}
          slot={selectedSlot}
          facings={panel.slots.flat().filter(s => s.product.key === selectedSlot.product.key)}
          panelLabel={panel.label}
          displayLabel={display.title}
          qty={quantities[selectedSlot.product.key] || 0}
          onChangeQuantity={(qty) => setQuantity(selectedSlot.product, qty)}
          onClose={() => setSelectedSlot(null)}
        />
      )}
      {reviewOpen && (
        <ReviewSheet
          lines={lines}
          onChangeQuantity={setQuantity}
          onClose={() => setReviewOpen(false)}
          onCopy={() => toast.success('Use Preview Order to submit')}
          onDownload={() => toast.success('Use Preview Order to submit')}
          onClear={() => {
            updateOrderData({ vegetableItems: [], flowerItems: [], heritageItems: [], livingstonItems: [] });
            setReviewOpen(false);
          }}
        />
      )}
    </Wrap>
  );
};
